import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  NOTE_MAX_LENGTH, validatePayBandForm,
  type PayBandForm as PayBandFormValues, type PayBandFormKey, type PayBandIssue,
} from '../payBand';
import { PAY_BAND_REVIEW_STATEMENT, type PayBasis } from '../types';
import { FieldGuidance } from '../guidance/FieldGuidance';
import { fieldDef } from '../guidance/roleFieldRegistry';
import { ErrorSummary } from './ErrorSummary';

export interface PayBandFormProps {
  /** The values to start from. The review box always starts unchecked: a new version of a band is reviewed again. */
  initial: PayBandFormValues;
  submitLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Problems the server reported for a field (for example the review gate), shown beside the form's own checks. */
  serverIssues?: PayBandIssue[];
  /** Called only when the form passed its own checks. */
  onSubmit: (values: PayBandFormValues) => void;
  onCancel: () => void;
  /** Move focus to the form heading when it opens (true for the Compensation tab; the wizard moves focus to its own step heading). */
  focusOnMount?: boolean;
  /** Banners (a conflict, a server refusal) shown between the summary and the fields. */
  children?: ReactNode;
  title?: string;
}

/**
 * The employer pay band form: basis, minimum, midpoint, maximum, the annual-hours assumption (shown and editable), a note, and the
 * required review acknowledgement. A validation summary lists what to fix and each item moves focus to its field. Nothing here
 * suggests a number: the person types their own band. The server re-checks every rule; this only saves a round trip.
 */
export function PayBandForm({ initial, submitLabel, cancelLabel = 'Cancel', busy = false, serverIssues = [], onSubmit, onCancel, focusOnMount = true, children, title = 'Employer pay band' }: PayBandFormProps) {
  const uid = useId();
  const [values, setValues] = useState<PayBandFormValues>(initial);
  const [issues, setIssues] = useState<PayBandIssue[]>([]);
  const summaryRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (focusOnMount) headingRef.current?.focus();
  }, [focusOnMount]);

  const shown = [...issues, ...serverIssues.filter((s) => !issues.some((i) => i.key === s.key))];
  const firstServerIssue = serverIssues[0]?.message;
  useEffect(() => {
    if (issues.length > 0) summaryRef.current?.focus();
  }, [issues]);
  useEffect(() => {
    if (firstServerIssue) summaryRef.current?.focus();
  }, [firstServerIssue]);

  const set = <K extends PayBandFormKey>(key: K, value: PayBandFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setIssues((prev) => prev.filter((i) => i.key !== key));
  };
  const errorOf = (key: PayBandFormKey) => shown.find((i) => i.key === key)?.message;
  const focusField = (key: string) => rootRef.current?.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();

  function submit(e: FormEvent) {
    e.preventDefault();
    const found = validatePayBandForm(values);
    setIssues(found);
    if (found.length === 0) onSubmit(values);
  }

  const unit = values.basis === 'Hourly' ? 'per hour' : 'per year';

  const moneyField = { minimum: 'payBandMinimum', midpoint: 'payBandMidpoint', maximum: 'payBandMaximum' } as const;
  const money = (key: 'minimum' | 'midpoint' | 'maximum', label: string) => {
    const id = `${uid}-${key}`;
    const err = errorOf(key);
    return (
      <div className="jr-field">
        <div className="jr-fieldhead">
          <label htmlFor={id}>{label} ({unit}) <span aria-hidden="true" className="jr-req">*</span></label>
          <FieldGuidance field={fieldDef(moneyField[key])} />
        </div>
        <input
          id={id} data-field={key} type="text" inputMode="decimal" autoComplete="off" value={values[key]} disabled={busy} aria-required="true"
          aria-invalid={err ? true : undefined} aria-describedby={err ? `${id}-err` : undefined} onChange={(e) => set(key, e.target.value)}
        />
        {err && <p id={`${id}-err`} className="jr-error">{err}</p>}
      </div>
    );
  };

  const hoursErr = errorOf('annualHours');
  const noteErr = errorOf('note');
  const reviewErr = errorOf('reviewed');

  return (
    <form ref={rootRef} className="jr-payband-form" onSubmit={submit} noValidate aria-labelledby={`${uid}-title`}>
      <h4 id={`${uid}-title`} ref={headingRef} tabIndex={-1} className="jr-form-title">{title}</h4>
      <ErrorSummary ref={summaryRef} issues={shown.map((i) => ({ key: i.key, message: i.message }))} onSelect={focusField} />
      {children}

      <fieldset className="jr-choice" disabled={busy}>
        <legend>Pay basis</legend>
        <FieldGuidance field={fieldDef('payBandBasis')} />
        {(['Hourly', 'Annual'] as PayBasis[]).map((b) => (
          <label key={b}>
            <input type="radio" name={`${uid}-basis`} data-field={b === 'Hourly' ? 'basis' : undefined} checked={values.basis === b} onChange={() => set('basis', b)} />
            {b === 'Hourly' ? 'Hourly (a rate per hour)' : 'Annual (a salary per year)'}
          </label>
        ))}
      </fieldset>

      <div className="jr-grid jr-grid--three">
        {money('minimum', 'Minimum')}
        {money('midpoint', 'Midpoint')}
        {money('maximum', 'Maximum')}
      </div>

      <div className="jr-grid">
        <div className="jr-field">
          <div className="jr-fieldhead">
            <label htmlFor={`${uid}-hours`}>Annual hours assumption <span aria-hidden="true" className="jr-req">*</span></label>
            <FieldGuidance field={fieldDef('payBandAnnualHours')} />
          </div>
          <input
            id={`${uid}-hours`} data-field="annualHours" type="text" inputMode="decimal" autoComplete="off" value={values.annualHours} disabled={busy} aria-required="true"
            aria-invalid={hoursErr ? true : undefined} aria-describedby={`${uid}-hours-hint${hoursErr ? ` ${uid}-hours-err` : ''}`} onChange={(e) => set('annualHours', e.target.value)}
          />
          <p id={`${uid}-hours-hint`} className="jr-hint">Used only to convert this band between hourly and annual amounts. 2,080 is a 40-hour week for 52 weeks.</p>
          {hoursErr && <p id={`${uid}-hours-err`} className="jr-error">{hoursErr}</p>}
        </div>
        <div className="jr-field">
          <div className="jr-fieldhead">
            <label htmlFor={`${uid}-note`}>Note (optional)</label>
            <FieldGuidance field={fieldDef('payBandNote')} />
          </div>
          <textarea
            id={`${uid}-note`} data-field="note" rows={2} value={values.note} disabled={busy} aria-invalid={noteErr ? true : undefined}
            aria-describedby={`${uid}-note-hint${noteErr ? ` ${uid}-note-err` : ''}`} onChange={(e) => set('note', e.target.value)}
          />
          <p id={`${uid}-note-hint`} className="jr-hint">Up to {NOTE_MAX_LENGTH} characters. Do not include personal information about any person.</p>
          {noteErr && <p id={`${uid}-note-err`} className="jr-error">{noteErr}</p>}
        </div>
      </div>

      <div className="jr-field jr-review">
        <label className="jr-review__label">
          <input
            type="checkbox" data-field="reviewed" checked={values.reviewed} disabled={busy} aria-required="true" aria-invalid={reviewErr ? true : undefined}
            aria-describedby={reviewErr ? `${uid}-review-err` : undefined} onChange={(e) => set('reviewed', e.target.checked)}
          />
          <span>{PAY_BAND_REVIEW_STATEMENT}</span>
        </label>
        {reviewErr && <p id={`${uid}-review-err`} className="jr-error">{reviewErr}</p>}
      </div>

      <div className="jr-actions">
        <button type="submit" className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className="jr-btn jr-btn--secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
      </div>
    </form>
  );
}
